import React, { useRef, useState } from 'react';
import { debug } from "../../../utils/debugLogger";

interface VideoPreviewPlayerProps {
  videoUrl: string;
  videoId?: string;
  onPublish?: () => void;
  onSchedule?: () => void;
  isPublishing?: boolean;
  isScheduling?: boolean;
}

export const VideoPreviewPlayer: React.FC<VideoPreviewPlayerProps> = ({
  videoUrl,
  videoId,
  onPublish,
  onSchedule,
  isPublishing = false,
  isScheduling = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePlayPause = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch((err) => {
        debug.error('Error playing video:', err);
        setError('Failed to play video');
      });
    }
  };

  return (
    <div className="rounded-lg overflow-hidden bg-[#1a1a1a]">
      {/* Video player */}
      <div className="relative">
        {error ? (
          <div className="aspect-video flex items-center justify-center bg-[#2a2a2a] text-white/60">
            <div className="text-center">
              <svg
                className="w-12 h-12 mx-auto mb-2 opacity-50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        ) : (
          <div className="aspect-video flex w-full items-center justify-center bg-black p-2 sm:p-3">
            <video
              ref={videoRef}
              src={videoUrl}
              className="max-h-full max-w-full object-contain object-center"
              controls
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            onError={() => setError('Failed to load video')}
            />
          </div>
        )}
      </div>

      {/* Action buttons */}
      {(onPublish || onSchedule) && (
        <div className="p-3 bg-[#2f4941] flex items-center justify-between gap-2">
          <div className="text-xs text-white/60">
            Your video is ready!
          </div>
          <div className="flex gap-2">
            {onSchedule && (
              <button
                type="button"
                onClick={onSchedule}
                disabled={isScheduling || isPublishing}
                className="px-3 py-1.5 bg-[#e4b372] text-[#2f4941] text-sm font-medium rounded-lg hover:bg-[#d9a865] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {isScheduling ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-[#2f4941]"></div>
                    Scheduling...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    Schedule
                  </>
                )}
              </button>
            )}
            {onPublish && (
              <button
                type="button"
                onClick={onPublish}
                disabled={isPublishing || isScheduling}
                className="px-3 py-1.5 bg-[#2f4941] text-white text-sm font-medium rounded-lg hover:bg-[#1e3128] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {isPublishing ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                    Publishing...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                      />
                    </svg>
                    Publish
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPreviewPlayer;
